import { ref, update } from "firebase/database";
import { IRoom, WithId } from "../@types/room";
import { INode } from "../@types/device";
import { ISource } from "../@types/source";
import { SourceKind } from "../enum";
import { db } from "../firebase";
import { asArray } from "./node";

type Map<T> = { [x: string]: T } | null | undefined;

/**
 * RTDB returns null for an empty path, so every caller that did
 * `Object.keys(map)` used to crash on a fresh database. Tolerating null here
 * (as well as at the listener) keeps that from ever coming back.
 */
export const toList = <T,>(m: Map<T>): WithId<T>[] =>
  m ? Object.entries(m).map(([id, v]) => ({ ...(v as T), id })) : [];

export const byOrder = (a: any, b: any): number =>
  (a.order ?? 0) - (b.order ?? 0) ||
  String(a.name ?? a.title ?? "").localeCompare(String(b.name ?? b.title ?? ""));

export const allRooms = (r: Map<IRoom>): WithId<IRoom>[] =>
  toList(r).sort(byOrder);

export const activeRooms = (r: Map<IRoom>): WithId<IRoom>[] =>
  allRooms(r).filter((x) => !x.disabled);

export const allNodes = (d: Map<INode>): WithId<INode>[] =>
  toList(d).sort(byOrder);

export const nodesOfRoom = (d: Map<INode>, roomId: string): WithId<INode>[] =>
  allNodes(d).filter((n) => n.roomId === roomId);

export const activeNodes = (d: Map<INode>, roomId: string): WithId<INode>[] =>
  nodesOfRoom(d, roomId).filter((n) => !n.disabled);

/** Matches on slug first, then falls back to the push key, so old links resolve. */
export const findRoom = (
  r: Map<IRoom>,
  param?: string
): WithId<IRoom> | undefined => {
  if (!param) return undefined;
  return (
    allRooms(r).find((x) => x.slug === param) ??
    (r?.[param] ? { ...r[param], id: param } : undefined)
  );
};

/** undefined means the roomId is orphaned -- callers flag that, they don't crash. */
export const roomName = (r: Map<IRoom>, id: string): string | undefined =>
  r?.[id]?.name;

/** Orders are spaced by 10 so a row can be inserted without renumbering. */
export const nextOrder = (list: Array<{ order?: number }>): number =>
  list.reduce((m, x) => Math.max(m, x.order ?? 0), -10) + 10;

// --- sources ----------------------------------------------------------------

export const allSources = (e: Map<ISource>): WithId<ISource>[] =>
  toList(e).sort(byOrder);

/**
 * Normalizes a row read from the database. Defaults to ENGINE on purpose: a
 * missing or hand-mangled metadata row must not silently turn a working board
 * read-only.
 */
export const kindOf = (s?: ISource): SourceKind =>
  s?.kind === SourceKind.TELEMETRY ? SourceKind.TELEMETRY : SourceKind.ENGINE;

export const isTelemetry = (e: Map<ISource>, addr?: string): boolean =>
  !!addr && kindOf(e?.[addr]) === SourceKind.TELEMETRY;

export const nodesOfSource = (d: Map<INode>, addr: string): WithId<INode>[] =>
  allNodes(d).filter((n) => n.addr === addr);

/**
 * Every source id the devices reference, from all five places it can appear.
 * A naive distinct-of-`node.addr` would miss a source that only ever shows up
 * as a sensor source -- WidgetParams writes addr:"data" into ac.indoor,
 * ac.outdoor and ac.runningAddr.
 */
export const usedAddrs = (d: Map<INode>): string[] => {
  const s = new Set<string>(["data"]);
  allNodes(d).forEach((n) => {
    if (n.addr) s.add(n.addr);
    asArray(n.reads).forEach((r) => r.addr && s.add(r.addr));
    asArray(n.inputs).forEach((i) => i.addr && s.add(i.addr));
    [n.ac?.indoor?.addr, n.ac?.outdoor?.addr, n.ac?.runningAddr].forEach(
      (a) => a && s.add(a)
    );
  });
  return Array.from(s).sort();
};

// --- ordering ---------------------------------------------------------------

/**
 * Swap two neighbours' order values rather than renumbering the whole list.
 * Identical in all three admin tables.
 */
export const swapOrder = (
  path: "rooms" | "devices" | "sources",
  list: Array<WithId<{ order?: number }>>,
  i: number,
  dir: -1 | 1
): Promise<void> | undefined => {
  const a = list[i];
  const b = list[i + dir];
  if (!a || !b) return;
  return update(ref(db), {
    [`${path}/${a.id}/order`]: b.order ?? i + dir,
    [`${path}/${b.id}/order`]: a.order ?? i,
  });
};
